/**
 * Bulk order matrix.
 *
 * Adds every filled row, plus the surcharge companion each row needs, in two
 * POSTs to /cart/add.js (rows, then companions). The Athens bulk table it replaces posts to
 * /cart/update.js with `updates:`, which cannot carry line-item properties and
 * cannot express two rows of the same variant with different options; `items[]`
 * can do both.
 *
 * Rows carry the same property names as the single-product configurator, read
 * from the same locale strings, so a matrix order and a configurator order look
 * identical downstream. Surcharges use the same companion-line mechanism and the
 * same _config_parent / _config_unit properties, so cart-surcharge-sync keeps
 * matrix rows in step too, with no extra code.
 *
 * One ordering constraint: a companion must name its parent's cart line key, and
 * that key does not exist until the parent has been added. So padlock rows go
 * first in one request, and their companions follow in a second, keyed off the
 * response. Both are still one user action.
 */
class BulkMatrix extends HTMLElement {
  connectedCallback() {
    this.configurable = this.dataset.configurable === "true";
    this.numberingPrice = parseInt(this.dataset.numberingPrice, 10) || 0;
    this.unitVariantId = this.dataset.unitVariant;
    // One unit's price in the visitor's currency, in cents (snippets/dc-unit-price.liquid): the
    // surcharges are counted in units, and a unit is CHF 1.00 but €1.10 on .de/.eu (a fixed
    // price in the store since 2026-10-13; converted and rounded it was €1.95).
    this.unitCents = parseInt(this.dataset.unitPrice, 10) || 100;

    this.rows = Array.from(this.querySelectorAll("[data-row]"));
    this.languageSelect = this.querySelector("[data-bulk-language]");
    this.submitButton = this.querySelector("[data-matrix-submit]");
    this.countEl = this.querySelector("[data-matrix-count]");
    this.totalEl = this.querySelector("[data-matrix-total]");
    this.surchargeEl = this.querySelector("[data-matrix-surcharge]");
    this.errorEl = this.querySelector("[data-matrix-error]");

    this.addEventListener("change", this.onInput.bind(this));
    this.addEventListener("input", this.onInput.bind(this));
    this.submitButton?.addEventListener("click", this.onSubmit.bind(this));
    // Folded rows (narrow card, decision row 91): the toggle opens the row's options.
    this.addEventListener("click", (event) => {
      const row = event.target.closest("[data-row-options]")?.closest("[data-row]");
      if (row) this.setRowOpen(row, !row.hasAttribute("data-open"));
    });

    this.render();
  }

  onInput(event) {
    // A typed quantity is settled to a whole number of at least 0 once the field is left.
    if (event?.type === "change" && event.target?.matches?.("[data-row-quantity]")) {
      const typed = Math.max(0, parseInt(event.target.value, 10) || 0);
      if (event.target.value !== String(typed)) event.target.value = String(typed);
    }
    // Keys per lock is offered on every row whatever its keying (Sam, 2026-09-21), so a key-type
    // change no longer disables or resets the row's key count.
    // The numbering text follows its tick: hidden AND disabled while off, so an empty value is
    // never read as numbering and the field is exempt from validation until it is on screen.
    this.rows.forEach((row) => {
      const on = row.querySelector("[data-row-numbering-on]")?.checked === true;
      const text = row.querySelector("[data-row-numbering]");
      if (!text) return;
      text.hidden = !on;
      text.disabled = !on;
      if (on && event?.target?.matches?.("[data-row-numbering-on]") && text.closest("[data-row]") === event.target.closest("[data-row]")) text.focus();
    });
    this.setError("");
    this.render();
  }

  setRowOpen(row, open) {
    if (!row) return;
    row.toggleAttribute("data-open", open);
    row.querySelector("[data-row-options]")?.setAttribute("aria-expanded", String(open));
  }

  /** A row's chosen options whatever its quantity (readRow is null at zero), for the summary. */
  rowOptions(row) {
    const keyType = row.querySelector("[data-row-key-type]")?.selectedOptions[0]?.dataset.keyType || null;
    const keysSelect = row.querySelector("[data-row-keys]");
    const keysLabel = keysSelect && !keysSelect.disabled ? keysSelect.selectedOptions[0]?.textContent.trim() || null : null;
    const numberingOn = row.querySelector("[data-row-numbering-on]")?.checked === true;
    const numbering = numberingOn ? row.querySelector("[data-row-numbering]")?.value.trim() || "" : "";
    return { keyType, keysLabel, numberingOn, numbering };
  }

  /**
   * The folded row's summary beside its Options toggle: "KD · 1 key", "KA · 3 keys · “A12”".
   * KD / KA as the pills print them; a ticked numbering with no text yet shows the option's name.
   */
  summaryText({ keyType, keysLabel, numberingOn, numbering }) {
    const parts = [];
    if (keyType) parts.push(keyType === "alike" ? "KA" : "KD");
    if (keysLabel) parts.push(keysLabel);
    if (numberingOn) parts.push(numbering ? `\u201c${numbering}\u201d` : this.dataset.propNumbering || "");
    return parts.filter(Boolean).join(" \u00b7 ");
  }

  /** Per-row state, used for both the running total and the cart payload. */
  readRow(row) {
    const quantity = parseInt(row.querySelector("[data-row-quantity]")?.value, 10) || 0;
    if (quantity <= 0) return null;

    const price = parseInt(row.dataset.price, 10) || 0;
    // "alike" / "different" from the option's own marker, never from its (translated) value:
    // comparing the value with the word "alike" was always false, so every matrix line posted
    // Keyed Different whatever pill was picked (fixed 2026-09-22).
    const keySelect = row.querySelector("[data-row-key-type]");
    const keyType = keySelect ? keySelect.selectedOptions[0]?.dataset.keyType || null : null;
    const keysSelect = row.querySelector("[data-row-keys]");
    const keysOption =
      keysSelect && !keysSelect.disabled ? keysSelect.selectedOptions[0] : null;

    // Numbering per colour (Sam, 2026-09-22): the row's tick turns it on and reveals the
    // text field; a ticked row is charged the per-padlock numbering price, the same rule the
    // single-product configurator applies to Yes/No. Until then one batch note outside the rows
    // was queried from inside each row, so no matrix line ever posted or paid for numbering.
    const numberingOn = row.querySelector("[data-row-numbering-on]")?.checked === true;
    const numberingInput = row.querySelector("[data-row-numbering]");
    const numbering = numberingOn ? numberingInput?.value.trim() || "" : "";

    const keysSurcharge = keysOption ? parseInt(keysOption.dataset.surcharge, 10) || 0 : 0;
    const numberingSurcharge = numberingOn ? this.numberingPrice : 0;
    const surcharge = keysSurcharge + numberingSurcharge;

    return {
      variantId: row.dataset.variantId,
      variantTitle: row.querySelector(".bulk-matrix-variant")?.firstChild?.textContent?.trim() || "",
      quantity,
      price,
      keyType,
      keysLabel: keysOption ? keysOption.textContent.trim() : null,
      numberingOn,
      numbering,
      keysSurcharge,
      numberingSurcharge,
      surcharge,
    };
  }

  filledRows() {
    return this.rows.map((row) => this.readRow(row)).filter(Boolean);
  }

  render() {
    const rows = this.filledRows();
    const units = rows.reduce((n, r) => n + r.quantity, 0);
    const goods = rows.reduce((n, r) => n + r.quantity * r.price, 0);
    const surcharge = rows.reduce((n, r) => n + r.quantity * r.surcharge * this.unitCents, 0);

    this.rows.forEach((row) => {
      const summary = row.querySelector("[data-row-summary]");
      if (summary) summary.textContent = this.summaryText(this.rowOptions(row));
      const state = this.readRow(row);
      const cell = row.querySelector("[data-row-total]");
      if (cell) {
        cell.textContent = state
          ? this.formatMoney(state.quantity * (state.price + state.surcharge * this.unitCents))
          : "";
      }
    });

    if (this.countEl) {
      this.countEl.textContent = String(units);
    }
    if (this.totalEl) {
      this.totalEl.textContent = this.formatMoney(goods + surcharge);
    }
    if (this.surchargeEl) {
      this.surchargeEl.hidden = surcharge <= 0;
      this.surchargeEl.textContent =
        surcharge > 0
          ? (this.dataset.totalLabel || "[price]").replace("[price]", this.formatMoney(surcharge))
          : "";
    }

    if (this.submitButton) this.submitButton.disabled = units <= 0;
  }

  /**
   * The label language every row posts: the buy box's checked pill, the control the customer
   * actually sees ("Label language applies to every line"). The band's own select is visually
   * hidden and only the fallback for a page without the pills. Nothing set that select until
   * 2026-10-01, so every matrix line posted English whatever pill was picked. Both carry the same
   * `products.configurator.language_xx` strings, so the values need no mapping.
   */
  labelLanguage() {
    const pill = document.querySelector?.("[data-config-language]:checked");
    return pill ? pill.value : this.languageSelect?.value;
  }

  /** Line-item properties for one row, named exactly as the configurator names them. */
  rowProperties(state) {
    const properties = {};

    if (this.configurable) {
      if (this.languageSelect) {
        properties[this.dataset.propLanguage] = this.labelLanguage();
      }

      if (state.keyType) {
        properties[this.dataset.propKeyType] =
          state.keyType === "alike" ? this.dataset.valueAlike : this.dataset.valueDifferent;
      }

      if (state.keysLabel) {
        properties[this.dataset.propKeys] = state.keysLabel;
      }

      // Yes/No plus the text, so a matrix line reads the same as a configurator
      // line and the warehouse sees one shape, not two.
      if (this.numberingPrice > 0) {
        properties[this.dataset.propNumbering] = state.numbering
          ? this.dataset.valueYes
          : this.dataset.valueNo;

        if (state.numbering) {
          properties[this.dataset.propNumberingText] = state.numbering;
        }
      }
    }

    return properties;
  }

  async onSubmit() {
    const rows = this.filledRows();
    if (rows.length === 0) return;

    this.setError("");

    // A ticked numbering with nothing to engrave would be a paid, unfulfillable line.
    const missing = this.rows.find((row) => {
      const state = this.readRow(row);
      return state && state.numberingOn && !state.numbering;
    });
    if (missing) {
      this.setError(this.dataset.numberingRequired || "");
      // A folded row hides its field, and a hidden field cannot take focus.
      this.setRowOpen(missing, true);
      missing.querySelector("[data-row-numbering]")?.focus();
      return;
    }
    this.submitButton.disabled = true;
    this.submitButton.classList.add("loading");

    // Each row's properties are read once and used both to add it and to find the line it made,
    // so a pill changed while the first request is in flight cannot unpair a companion.
    const posted = rows.map((state) => this.rowProperties(state));

    try {
      // 1. The padlock rows, one request.
      const added = await this.addItems(
        rows.map((state, index) => ({
          id: state.variantId,
          quantity: state.quantity,
          properties: posted[index],
        })),
      );

      // 2. Their companions, keyed off the line keys the response returns.
      //    Matched by variant id AND properties rather than by position: the
      //    Ajax API documents the response as "the line items associated with
      //    the added items" but does not promise they come back in request
      //    order, and pairing the wrong companion to the wrong row would charge
      //    a customer for options they did not choose. Matching on identity is
      //    correct whatever the order.
      const companions = [];
      rows.forEach((state, index) => {
        if (state.surcharge <= 0) return;
        const parent = this.matchLine(added.items, state, posted[index]);
        if (!parent) return;

        // Hidden properties carry the numbers the cart and the sync need; the visible ones
        // read on the checkout page, where this line cannot be folded into the padlock's
        // (Sam, 2026-09-22): what it is for, and each charge per padlock.
        const per = this.dataset.perPadlock || "";
        const properties = {
          _config_parent: parent.key,
          _config_unit: String(state.surcharge),
          _config_numbering: String(state.numberingSurcharge),
          _config_keys: String(state.keysSurcharge),
          [this.dataset.forLabel]: `${this.dataset.productTitle}, ${state.variantTitle}`,
        };
        if (state.numberingSurcharge > 0) {
          properties[this.dataset.propNumbering] = `${this.formatMoney(state.numberingSurcharge * this.unitCents)} ${per}`.trim();
        }
        if (state.keysSurcharge > 0) {
          properties[this.dataset.propKeys] = `${state.keysLabel ? state.keysLabel + ", " : ""}${this.formatMoney(state.keysSurcharge * this.unitCents)} ${per}`.trim();
        }
        companions.push({
          id: this.unitVariantId,
          quantity: state.surcharge * state.quantity,
          properties,
        });
      });

      if (companions.length > 0) await this.addItems(companions);

      this.resetRows();

      document.dispatchEvent(
        new CustomEvent("cart:item-add", {
          detail: { items: added.items || [], context: "BulkMatrix" },
        }),
      );
    } catch (error) {
      console.error("bulk matrix:", error);
      this.setError(error.message || window.cartStrings?.error || "");
    } finally {
      this.submitButton.classList.remove("loading");
      this.render();
    }
  }

  /**
   * After an add: every quantity back to 0 and every numbering tick off. Keying and keys stay as
   * chosen, so a second batch with the same options needs only quantities.
   */
  resetRows() {
    this.rows.forEach((row) => {
      const input = row.querySelector("[data-row-quantity]");
      if (input) {
        input.value = "0";
        // dc-stepper redraws only on its control's change event, so without this the rows kept
        // showing the added quantities while their inputs read 0 (fixed 2026-10-01). It does not
        // bubble, so this element's own listener does not run once per row; onInput runs once.
        input.dispatchEvent(new Event("change"));
      }
      const tick = row.querySelector("[data-row-numbering-on]");
      if (tick) tick.checked = false;
      const text = row.querySelector("[data-row-numbering]");
      if (text) text.value = "";
    });
    this.onInput();
  }

  /**
   * Find the cart line a row produced, by variant and by the exact properties
   * the row posted. Two rows of the same variant differ only by their
   * properties, so both parts are needed.
   */
  matchLine(lines, state, wanted = this.rowProperties(state)) {
    if (!Array.isArray(lines)) return null;

    return (
      lines.find((line) => {
        if (String(line.variant_id || line.id) !== String(state.variantId)) return false;

        const actual = line.properties || {};
        const keys = Object.keys(wanted);
        if (keys.length !== Object.keys(actual).filter((k) => !k.startsWith("_")).length) {
          return false;
        }
        return keys.every((key) => actual[key] === wanted[key]);
      }) || null
    );
  }

  async addItems(items) {
    const response = await fetch(window.routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });

    const body = await response.json();
    // Shopify answers a rejected add with 4xx and a description; surface that
    // rather than failing silently with a cart that did not change.
    if (!response.ok) throw new Error(body.description || body.message || "");
    return body;
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = !message;
    this.errorEl.textContent = message;
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || "{{amount}}";
    const value = cents / 100;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case "amount":
          return this.withSeparators(value.toFixed(2), ",", ".");
        case "amount_no_decimals":
          return this.withSeparators(Math.round(value).toString(), ",", "");
        case "amount_with_comma_separator":
          return this.withSeparators(value.toFixed(2), ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return this.withSeparators(Math.round(value).toString(), ".", "");
        default:
          return value.toFixed(2);
      }
    });
  }

  withSeparators(number, thousands, decimal) {
    const [whole, fraction] = number.split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }
}

customElements.define("bulk-matrix", BulkMatrix);
